import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { useEffect, useState, VFC } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';

interface ParamsHttpRequestHeadersProps {
  editable: boolean;
  headers: Record<string, string>[];
  updateHeaders: (headers: Record<string, string>[]) => void;
  jsonSchema: JsonSchema;
  tokensToDisplayValue: Record<string, string>;
  allowedTypes: JsonSchemaTypes[];
}

const HeaderRow = styled.div`
  display: grid;
  grid-auto-columns: 45% 45% 5%;
  grid-auto-flow: column;
  column-gap: ${Spacing[2]};

  &:not(:last-child) {
    margin-bottom: ${Spacing[2]};
  }
`;

const KeyInput = styled(Input)`
  margin-right: ${Spacing[2]};
`;

const CenteredIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  cursor: pointer;
`;

const ParamsHttpRequestHeaders: VFC<ParamsHttpRequestHeadersProps> = ({
  editable,
  headers,
  updateHeaders,
  jsonSchema,
  tokensToDisplayValue,
  allowedTypes,
}) => {
  const [keys, setKeys] = useState(headers.map(() => uuid()));
  useEffect(() => {
    const newKeys = keys.slice();
    headers.forEach((_, index) => {
      if (!newKeys[index]) {
        newKeys.push(uuid());
      }
    });
    setKeys(newKeys);
  }, [headers.length]);

  const updateHeaderKey = (idx: number, newKey: string) => {
    const newHeaders = [...headers];
    newHeaders[idx] = { key: newKey, value: newHeaders[idx].value };
    updateHeaders(newHeaders);
  };
  const updateHeaderValue = (idx: number, newValue: string) => {
    const newHeaders = [...headers];
    newHeaders[idx] = { key: newHeaders[idx].key, value: newValue };
    updateHeaders(newHeaders);
  };

  const addNewHeader = () => {
    updateHeaders([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (idx: number) => {
    const newHeaders = [...headers];
    newHeaders.splice(idx, 1);
    updateHeaders(newHeaders);
  };

  return (
    <>
      <ParamLabel labelText='Request Headers' />
      {headers.map((record, idx) => (
        <HeaderRow key={keys[idx]}>
          <KeyInput
            type='string'
            size='md'
            key={`header-key-${idx}`}
            value={record.key}
            onChange={(e) => updateHeaderKey(idx, e.target.value)}
            disabled={!editable}
            placeholder='Key'
            id={`workflow-http-request-header__edit-key-${idx}`}
          ></KeyInput>
          <HandlebarFormControl
            size='md'
            type='input'
            placeholder='Value'
            value={record.value}
            updateValue={(value: string) => {
              updateHeaderValue(idx, value);
            }}
            editable={editable}
            allowedTypes={allowedTypes}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            id={`workflow-http-request-header__edit-value-${idx}`}
          />
          {editable && (
            <CenteredIcon
              id={`workflow-http-request-header__delete-${idx}`}
              aria-label={`Delete header field ${record.key}`}
              role='button'
              tabIndex={0}
              onClick={() => removeHeader(idx)}
            >
              <XIcon />
            </CenteredIcon>
          )}
        </HeaderRow>
      ))}
      <Spacer height={2} />
      <div>
        <Button
          variant='basic'
          width='auto'
          disabled={!editable}
          onClick={addNewHeader}
          id='workflow-http-request-header__add'
          aria-label='Add a new header'
        >
          Add Header
        </Button>
      </div>
    </>
  );
};

export default ParamsHttpRequestHeaders;
